import { SELECT_CONTACT, CREATE_GROUP_CONTACT } from 'actions';
import Notifier from 'common/helpers/Notifier';
import { isHttpSuccess } from 'src/api/utils/httpStatus';
import { coreApi, coreClientServiceApi } from 'src/api/config';
import _ from 'lodash';
import { getGeocodedAddress } from 'src/components/Browse/Map/utils/geoCode';

const geocodeAddresses = async (addresses) => {
  if (!addresses || !addresses.length) return [];

  try {
    const geocodedAddresses = await Promise.all(
      addresses.map(async (address) => {
        const geocodedAddress = await getGeocodedAddress(address);

        return {
          address_type: geocodedAddress.address_type,
          line_1: geocodedAddress.line_1,
          line_2: geocodedAddress.line_2,
          city: geocodedAddress.city,
          state: geocodedAddress.state,
          country: geocodedAddress.country,
          postal_code: geocodedAddress.postal_code,
          county: geocodedAddress.county,
          latitude: geocodedAddress.latitude,
          longitude: geocodedAddress.longitude,
        };
      }),
    );
    return geocodedAddresses;
  } catch (error) {
    Notifier.handleErrors(error);
    return [];
  }
};

const createLanguages = async (personId, spoken = [], written = []) => {
  function mapLanguagesData(languages, type) {
    return languages.map((l) => (
      {
        language_id: l.id,
        record_id: personId,
        record_language_type: type,
        record_type: 'Person',
      }
    ));
  }
  const languagesData = [
    ...mapLanguagesData(spoken, 'spoken'),
    ...mapLanguagesData(written, 'written'),
  ];

  try {
    const languages = [];
    const promises = languagesData.map((data) => coreApi.createRecord('record_language', { ...data }));
    const responses = await Promise.all(promises);
    // eslint-disable-next-line consistent-return
    responses.forEach((response) => {
      const status = _.get(response, 'status');
      if (status && !isHttpSuccess(status)) {
        return Notifier.handleErrors(response);
      }

      languages.push({ ..._.get(response, 'data.data') });
    });
    return languages;
  } catch (error) {
    Notifier.handleErrors(error);
    return [];
  }
};

function createGroupContact({
  groupId,
  contact,
}) {
  return async (dispatch) => {
    try {
      let contact_preferences = { ...contact.contact_preferences };
      const voice_mail_ok = contact_preferences?.voice_mail_ok === 'true';
      contact_preferences = {
        ...contact_preferences,
        voice_mail_ok,
      };

      const geocodedAddresses = await geocodeAddresses(contact.addresses);

      const clientServicePayload = {
        person: {
          first_name: contact.first_name,
          last_name: contact.last_name,
          middle_name: contact.middle_name,
          preferred_name: contact.preferred_name,
          nicknames: contact.nicknames,
          date_of_birth: contact.date_of_birth,
          race: contact.race,
          gender: contact.gender,
          ethnicity: contact.ethnicity,
          citizenship: contact.citizenship || 'undisclosed',
          marital_status: contact.marital_status,
          gross_monthly_income: contact.gross_monthly_income,
          phone_numbers: contact.phone_numbers || [],
          email_addresses: contact.email_addresses || [],
          military: contact.military || {},
          household: contact.household || { total: 1 },
        },
        provider: groupId,
        addresses: geocodedAddresses,
      };

      const response = await coreClientServiceApi.createRecord('client', clientServicePayload);
      const person = response.data.data;

      await coreApi.createRecord('contact_preference', {
        ...contact_preferences,
        person: person.id,
      });

      const { preferred_spoken_languages, preferred_written_languages } = contact;
      await createLanguages(person.id, preferred_spoken_languages, preferred_written_languages);

      dispatch({
        type: CREATE_GROUP_CONTACT,
        payload: response,
      });

      dispatch({
        type: SELECT_CONTACT,
        id: person.id,
      });

      Notifier.dispatch(response.status, 'Client Successfully Created');

      return response;
    } catch (error) {
      Notifier.handleErrors(error);

      return false;
    }
  };
}

export default createGroupContact;
